import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { apiFetch, apiFetchJson } from '../../apiClient';
import BookCover from './BookCover';
import './ClubDetail.css';

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

function ClubDetail({ clubId, onBack, factFictionMode, navigateTo }) {
  const { instance } = useMsal();
  const { t } = useTranslation();
  const [club, setClub] = useState(null);
  const [reads, setReads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [joining, setJoining] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [nominateQuery, setNominateQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [nominating, setNominating] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const loadClub = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [clubRes, readsRes] = await Promise.all([
        apiFetch(instance, `/api/bookclubs/${clubId}`),
        apiFetch(instance, `/api/bookclubreads/${clubId}`),
      ]);
      if (clubRes.ok) {
        const data = await clubRes.json();
        setClub(data);
      } else {
        setError('Failed to load club details.');
        return;
      }
      if (readsRes.ok) {
        const data = await readsRes.json();
        setReads(unwrap(data));
      }
    } catch (e) {
      console.error('Failed to load club:', e);
      setError('An error occurred while loading the club.');
    } finally {
      setLoading(false);
    }
  }, [instance, clubId]);

  useEffect(() => { loadClub(); }, [loadClub]);

  const members = club ? unwrap(club.members) : [];
  const activeReads = club ? unwrap(club.activeReads) : [];
  const currentRead = activeReads.find(r => r.status === 'InProgress' || r.status === 'Active');
  const pastReads = reads.filter(r => r.status === 'Completed' || r.status === 'Finished');
  const account = instance.getActiveAccount();
  const isMember = members.some(m => m.userId === account?.localAccountId);

  const handleJoin = async () => {
    setJoining(true);
    try {
      await apiFetchJson(instance, `/api/bookclubs/${clubId}/join`, { method: 'POST' });
      await loadClub();
    } catch (e) {
      console.error('Failed to join club:', e);
      setError(e.message || 'An error occurred while joining.');
    } finally {
      setJoining(false);
    }
  };

  const handleLeave = async () => {
    if (!window.confirm(t('bookClub.clubDetail.leaveConfirm'))) return;
    setLeaving(true);
    try {
      await apiFetchJson(instance, `/api/bookclubs/${clubId}/leave`, { method: 'POST' });
      await loadClub();
    } catch (e) {
      console.error('Failed to leave club:', e);
      setError(e.message || 'An error occurred while leaving.');
    } finally {
      setLeaving(false);
    }
  };

  const handleSearchBooks = async () => {
    if (!nominateQuery.trim()) return;
    setSearching(true);
    try {
      const type = factFictionMode === 'fact' ? 'fact' : 'fiction';
      const data = await apiFetchJson(instance, `/api/books/search?q=${encodeURIComponent(nominateQuery.trim())}&type=${type}`);
      setSearchResults(unwrap(data));
    } catch (e) {
      console.error('Failed to search books:', e);
    } finally {
      setSearching(false);
    }
  };

  const handleNominate = async (bookId) => {
    setNominating(true);
    try {
      await apiFetchJson(instance, `/api/bookclubreads/${clubId}/nominate`, {
        method: 'POST',
        body: JSON.stringify({ bookId }),
      });
      setNominateQuery('');
      setSearchResults([]);
      await loadClub();
    } catch (e) {
      console.error('Failed to nominate:', e);
      setError(e.message || 'Failed to nominate book.');
    } finally {
      setNominating(false);
    }
  };

  const handleCreateInvite = async (sendEmail = false) => {
    setInviteSending(true);
    setInviteResult(null);
    try {
      const body = sendEmail && inviteEmail.trim() ? { email: inviteEmail.trim() } : {};
      const res = await apiFetch(instance, `/api/bookclubs/${clubId}/invite`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        setInviteLink(data.inviteLink);
        setInviteResult({
          success: true,
          emailSent: data.emailSent,
          emailConfigured: data.emailConfigured,
          expiresAt: data.expiresAt,
        });
        if (sendEmail && !data.emailSent && inviteEmail.trim()) {
          // Fall back to mailto: if ACS not configured
          const subject = encodeURIComponent(`📚 Join ${club.name} on Kosmoholm!`);
          const mailBody = encodeURIComponent(
            `Hey! I'd love for you to join my book club "${club.name}" on Kosmoholm.\n\n` +
            `Click here to join: ${data.inviteLink}\n\n` +
            `The invite expires in 7 days. Happy reading! 📖`
          );
          window.open(`mailto:${inviteEmail.trim()}?subject=${subject}&body=${mailBody}`, '_blank');
        }
      } else {
        const data = await res.json().catch(() => null);
        setInviteResult({ success: false, error: data?.error || 'Failed to create invite.' });
      }
    } catch (err) {
      setInviteResult({ success: false, error: err.message || 'Failed to create invite.' });
    } finally {
      setInviteSending(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = inviteLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const daysSince = (dateStr) => {
    if (!dateStr) return 0;
    const diff = Date.now() - new Date(dateStr).getTime();
    return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
  };

  if (loading) {
    return (
      <div className="club-detail-loading">
        <div className="club-detail-spinner" />
        <p>{t('bookClub.clubDetail.loading')}</p>
      </div>
    );
  }

  if (error && !club) {
    return (
      <div className="club-detail-error">
        <span>⚠️ {error}</span>
        <div className="club-detail-error-actions">
          <button onClick={loadClub}>↻ {t('bookClub.common.retry')}</button>
          <button onClick={onBack}>← {t('bookClub.common.back')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="club-detail">
      <button className="club-detail-back" onClick={onBack}>← {t('bookClub.clubDetail.backToClubs')}</button>

      {error && (
        <div className="club-detail-inline-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Hero Header */}
      <div className="club-detail-hero">
        <div className="club-detail-hero-info">
          <h1 className="club-detail-name">{club.name}</h1>
          {club.description && <p className="club-detail-desc">{club.description}</p>}
          <div className="club-detail-badges">
            <span className="club-detail-badge club-detail-badge-type">
              {club.bookType === 'Fact' ? '📚 Fact' : '📖 Fiction'}
            </span>
            {club.preferredPace && (
              <span className="club-detail-badge club-detail-badge-pace">
                🕐 {club.preferredPace}
              </span>
            )}
            <span className="club-detail-badge club-detail-badge-members">
              👥 {members.length}{club.maxMembers ? ` / ${club.maxMembers}` : ''}
            </span>
            {club.isPublic && <span className="club-detail-badge club-detail-badge-public">🌍 {t('bookClub.clubDetail.public')}</span>}
            {club.isHousehold && <span className="club-detail-badge club-detail-badge-household">🏠 {t('bookClub.clubDetail.household')}</span>}
          </div>
        </div>
        <div className="club-detail-hero-action">
          {isMember ? (
            <button className="club-detail-btn club-detail-btn-leave" onClick={handleLeave} disabled={leaving}>
              {leaving ? t('bookClub.clubDetail.leaving') : '🚪 ' + t('bookClub.clubDetail.leaveClub')}
            </button>
          ) : (
            <button className="club-detail-btn club-detail-btn-join" onClick={handleJoin} disabled={joining}>
              {joining ? t('bookClub.clubDetail.joining') : '✋ ' + t('bookClub.clubDetail.joinClub')}
            </button>
          )}
        </div>
      </div>

      {/* Members */}
      <section className="club-detail-section">
        <h2 className="club-detail-section-title">👥 {t('bookClub.clubDetail.members')}</h2>
        <div className="club-detail-members">
          {members.map((m) => (
            <div key={m.id} className="club-detail-member">
              <div className="club-detail-member-avatar">
                {(m.userName || '?').charAt(0).toUpperCase()}
              </div>
              <span className="club-detail-member-name">{m.userName || 'Unknown'}</span>
              <span className={`club-detail-member-role club-detail-role-${(m.role || 'member').toLowerCase()}`}>
                {m.role || t('bookClub.clubDetail.member')}
              </span>
            </div>
          ))}
          {members.length === 0 && <p className="club-detail-empty">{t('bookClub.clubDetail.noMembers')}</p>}
        </div>
      </section>

      {/* Invite Friends */}
      {isMember && (
        <section className="club-detail-section">
          <div className="club-detail-invite-header">
            <h2 className="club-detail-section-title">✉️ {t('bookClub.clubDetail.inviteFriends')}</h2>
            {!showInvite && (
              <button className="club-detail-btn club-detail-btn-invite" onClick={() => setShowInvite(true)}>
                + {t('bookClub.clubDetail.invite')}
              </button>
            )}
          </div>
          {showInvite && (
            <div className="club-detail-invite-panel">
              {!inviteLink ? (
                <>
                  <p className="club-detail-invite-desc">
                    Generate an invite link to share, or enter an email to send directly.
                  </p>
                  <div className="club-detail-invite-form">
                    <input
                      type="email"
                      className="club-detail-invite-input"
                      placeholder={t('bookClub.clubDetail.emailPlaceholder')}
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleCreateInvite(!!inviteEmail.trim())}
                    />
                    <div className="club-detail-invite-buttons">
                      <button
                        className="club-detail-btn club-detail-btn-join"
                        onClick={() => handleCreateInvite(false)}
                        disabled={inviteSending}
                      >
                        {inviteSending ? '…' : '🔗 ' + t('bookClub.clubDetail.getLink')}
                      </button>
                      {inviteEmail.trim() && (
                        <button
                          className="club-detail-btn club-detail-btn-email"
                          onClick={() => handleCreateInvite(true)}
                          disabled={inviteSending}
                        >
                          {inviteSending ? '…' : '📧 ' + t('bookClub.clubDetail.sendEmail')}
                        </button>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="club-detail-invite-result">
                  <div className="club-detail-invite-link-row">
                    <input
                      type="text"
                      className="club-detail-invite-link"
                      value={inviteLink}
                      readOnly
                      onClick={(e) => e.target.select()}
                    />
                    <button
                      className={`club-detail-btn ${linkCopied ? 'club-detail-btn-copied' : 'club-detail-btn-join'}`}
                      onClick={handleCopyLink}
                    >
                      {linkCopied ? '✓ ' + t('bookClub.clubDetail.copied') : '📋 ' + t('bookClub.clubDetail.copy')}
                    </button>
                  </div>
                  {inviteResult?.emailSent && (
                    <p className="club-detail-invite-success">✅ Email sent to {inviteEmail}</p>
                  )}
                  {inviteResult?.success && !inviteResult?.emailSent && inviteEmail.trim() && (
                    <p className="club-detail-invite-info">📬 Your email app opened with the invite link.</p>
                  )}
                  {inviteResult?.expiresAt && (
                    <p className="club-detail-invite-expiry">
                      ⏳ Expires {new Date(inviteResult.expiresAt).toLocaleDateString()}
                    </p>
                  )}
                  <button
                    className="club-detail-btn club-detail-btn-new-invite"
                    onClick={() => { setInviteLink(''); setInviteEmail(''); setInviteResult(null); }}
                  >
                    + {t('bookClub.clubDetail.createAnother')}
                  </button>
                </div>
              )}
              {inviteResult && !inviteResult.success && (
                <p className="club-detail-invite-error">⚠️ {inviteResult.error}</p>
              )}
            </div>
          )}
        </section>
      )}

      {/* Current Read */}
      {currentRead && (
        <section className="club-detail-section">
          <h2 className="club-detail-section-title">📖 {t('bookClub.clubDetail.currentlyReading')}</h2>
          <div className="club-detail-current-read">
            {currentRead.bookCoverImageUrl && (
              <img
                className="club-detail-cover"
                src={currentRead.bookCoverImageUrl}
                alt={currentRead.bookTitle}
              />
            )}
            <div className="club-detail-current-read-info">
              <h3 className="club-detail-book-title">{currentRead.bookTitle}</h3>
              <p className="club-detail-book-author">{t('bookClub.common.by')} {currentRead.bookAuthor}</p>
              {currentRead.startDate && (
                <p className="club-detail-reading-since">
                  📅 Reading since {formatDate(currentRead.startDate)} ({daysSince(currentRead.startDate)} days)
                </p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Past Reads */}
      {pastReads.length > 0 && (
        <section className="club-detail-section">
          <h2 className="club-detail-section-title">📚 {t('bookClub.clubDetail.pastReads')}</h2>
          <div className="club-detail-reads-grid">
            {pastReads.map((r) => (
              <div key={r.id} className="club-detail-read-card">
                {r.bookCoverImageUrl ? (
                  <BookCover src={r.bookCoverImageUrl} alt={r.bookTitle} className="club-detail-read-cover" emoji="📕" />
                ) : (
                  <div className="club-detail-read-cover-placeholder">📕</div>
                )}
                <div className="club-detail-read-info">
                  <span className="club-detail-read-title">{r.bookTitle}</span>
                  <span className="club-detail-read-author">{r.bookAuthor}</span>
                  {r.endDate && <span className="club-detail-read-date">Finished {formatDate(r.endDate)}</span>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Nominate a Book */}
      {isMember && (
        <section className="club-detail-section">
          <h2 className="club-detail-section-title">📝 {t('bookClub.clubDetail.nominateBook')}</h2>
          <div className="club-detail-nominate">
            <div className="club-detail-nominate-search">
              <input
                type="text"
                className="club-detail-nominate-input"
                placeholder={t('bookClub.clubDetail.nominatePlaceholder')}
                value={nominateQuery}
                onChange={(e) => setNominateQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchBooks()}
              />
              <button
                className="club-detail-btn club-detail-btn-search"
                onClick={handleSearchBooks}
                disabled={searching || !nominateQuery.trim()}
              >
                {searching ? '…' : '🔍'}
              </button>
            </div>
            {searchResults.length > 0 && (
              <div className="club-detail-search-results">
                {searchResults.map((book) => (
                  <div key={book.id} className="club-detail-search-result">
                    <div className="club-detail-search-result-info">
                      <span className="club-detail-search-result-title">{book.title}</span>
                      <span className="club-detail-search-result-author">{book.author}</span>
                    </div>
                    <button
                      className="club-detail-btn club-detail-btn-nominate"
                      onClick={() => handleNominate(book.id)}
                      disabled={nominating}
                    >
                      {nominating ? '…' : t('bookClub.clubDetail.nominate')}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

export default ClubDetail;
